import { RefObject } from 'react'
import Konva from 'konva'

import { Point2D, Vector2D } from '@ahha/stableComponents/Canvas/types/point'
import { KonvaEventObject } from 'konva/lib/Node'
import { CornerAnchors, EllipseSnapshot } from '@ahha/stableComponents/Canvas/types/shape'
import { isKonvaEvent } from '@ahha/stableComponents/Canvas/types/typeGuard'
import { DEFAULT_SNAPSHOT_ID } from '@ahha/stableComponents/Canvas/utils/hooks'
import { CIRCLE_SNAPSHOT_ID, MIN_ELLIPSE_SIZE } from '@ahha/stableComponents/Canvas/Ellipse/const'
import { isRotateAnchor } from '@ahha/stableComponents/Canvas/utils/konva'
import { PINNED_ANCHOR } from '@ahha/stableComponents/Canvas/const'

export const getEllipseInfo = (ellipse: Konva.Ellipse | null) => {
  if (!ellipse) {
    return undefined
  }
  return {
    cx: ellipse.x(),
    cy: ellipse.y(),
    rx: ellipse.width() / 2,
    ry: ellipse.height() / 2,
    rotation: ellipse.rotation(),
    scaleX: ellipse.scaleX(),
    scaleY: ellipse.scaleY(),
  }
}

export const updateEllipseInternalState = (
  node: KonvaEventObject<Event>| RefObject<Konva.Ellipse>,
  data: EllipseSnapshot,
  resetScale = true
) => {
  const shape = isKonvaEvent(node) ? node.target : node.current
  const { cx, cy, rx, ry, rotation, scaleX, scaleY } = data

  if (!shape) {
    return
  }
  shape.position({ x: cx, y: cy })
  shape.size({ width: rx * 2 * scaleX, height: ry * 2 * scaleY })
  shape.rotation(rotation)
  /** transform 도중 의도하지 않은 skew 제거 */
  shape.skew({ x: 0, y: 0 })

  if (resetScale) {
    shape.scale({ x: 1, y: 1 })
  }
}

/**
 * @see https://math.stackexchange.com/questions/91132/how-to-get-the-limits-of-rotated-ellipse
 */
export const getBoundingRectOfEllipse = ([cx, cy]: Point2D, rx: number, ry: number, rotation: number) => {
  const rad = rotation * (Math.PI / 180)

  const boundX = Math.sqrt((rx * Math.cos(rad)) ** 2 + (ry * Math.sin(rad)) ** 2)
  const boundY = Math.sqrt((rx * Math.sin(rad)) ** 2 + (ry * Math.cos(rad)) ** 2)

  return {
    x: cx - boundX,
    y: cy - boundY,
    width: boundX * 2,
    height: boundY * 2,
  }
}

export const getEllipseInitialSnapshot = (data: EllipseSnapshot) => ({
  [DEFAULT_SNAPSHOT_ID]: {
    cx: data.cx,
    cy: data.cy,
    rx: data.rx,
    ry: data.ry,
    rotation: data.rotation,
    scaleX: 1,
    scaleY: 1,
  },
  [CIRCLE_SNAPSHOT_ID]: {
    cx: data.cx,
    cy: data.cy,
    rx: Math.min(data.rx, data.ry),
    ry: Math.min(data.rx, data.ry),
    rotation: data.rotation,
    scaleX: 1,
    scaleY: 1,
  },
}) as const

export const updateDefaultSnapshot = (fixedPoint: Point2D, transformer: Konva.Transformer | null) => (data: EllipseSnapshot, prev: EllipseSnapshot) => {
  const anchor = transformer?.getActiveAnchor() ?? ''
  const fixedAnchor = PINNED_ANCHOR[anchor]

  if (isRotateAnchor(anchor)) {
    return { ...data, rotation: data.rotation }
  }
  const { rx, ry, scaleX, scaleY } = data
  const currentRx = rx * scaleX
  const currentRy = ry * scaleY

  const [px, py] = fixedPoint
  const [vx, vy] = getVectorToCenter(fixedAnchor, prev.rotation, currentRx, currentRy)

  return {
    ...data,
    /** Shift를 누른 상태에서 중간 지점 anchor를 움직이면 회전 변환이 아님에도 rotation이 달라지는 문제가 있어 rotation 고정 */
    rotation: prev.rotation,
    cx: px + vx,
    cy: py + vy,
  }
}

/** @deprecated 생성된 도형에 대해서는 Shift로 원 그리는 기능 제거됨. */
export const updateCircleSnapshot = (fixedAnchor?: CornerAnchors, fixedPoint?: Point2D) => (data: EllipseSnapshot, prev: EllipseSnapshot, defaultSnapshot: EllipseSnapshot) => {
  const { rx, ry, scaleX, scaleY } = defaultSnapshot

  const size = Math.max(MIN_ELLIPSE_SIZE, Math.min(rx * scaleX, ry * scaleY))
  const circleData = { rx: size, ry: size, scaleX: 1, scaleY: 1 }

  if (!fixedAnchor || !fixedPoint) {
    return { ...data, ...circleData }
  }
  const [ox, oy] = getCircleCenter(defaultSnapshot, fixedAnchor, fixedPoint)

  return {
    ...defaultSnapshot,
    ...circleData,
    cx: ox,
    cy: oy,
  }
}

export const getEllipseFixedPoint = (data: EllipseSnapshot, fixedAnchor: CornerAnchors): Point2D => {
  const { cx, cy, rx, ry, rotation, scaleX, scaleY } = data
  const rad = rotation * (Math.PI / 180)
  const scaledRx = rx * scaleX
  const scaledRy = ry * scaleY

  const cosA = Math.cos(rad)
  const sinA = Math.sin(rad)

  if (fixedAnchor === 'tl') {
    return [cx - scaledRx * cosA + scaledRy * sinA, cy - scaledRx * sinA - scaledRy * cosA]
  }
  if (fixedAnchor === 'tr') {
    return [cx + scaledRx * cosA + scaledRy * sinA, cy + scaledRx * sinA - scaledRy * cosA]
  }
  if (fixedAnchor === 'bl') {
    return [cx - scaledRx * cosA - scaledRy * sinA, cy - scaledRx * sinA + scaledRy * cosA]
  }
  if (fixedAnchor === 'br') {
    return [cx + scaledRx * cosA - scaledRy * sinA, cy + scaledRx * sinA + scaledRy * cosA]
  }
  return [cx, cy]
}

const getVectorToCenter = (
  fixedAnchor: CornerAnchors,
  rotation: number,
  rx: number,
  ry: number
): Vector2D => {
  const radian = rotation * (Math.PI / 180)
  const cosA = Math.cos(radian)
  const sinA = Math.sin(radian)

  if (fixedAnchor === 'tl') {
    return [rx * cosA - ry * sinA, ry * cosA + rx * sinA]
  }
  if (fixedAnchor === 'tr') {
    return [-rx * cosA - ry * sinA, ry * cosA - rx * sinA]
  }
  if (fixedAnchor === 'bl') {
    return [rx * cosA + ry * sinA, -ry * cosA + rx * sinA]
  }
  if (fixedAnchor === 'br') {
    return [-rx * cosA + ry * sinA, -ry * cosA - rx * sinA]
  }
  return [0, 0]
}

export const getCircleCenter = (data: EllipseSnapshot, fixedAnchor: CornerAnchors, fixedPoint: Point2D): Point2D => {
  const { rx, ry, rotation, scaleX, scaleY } = data
  const size = Math.max(MIN_ELLIPSE_SIZE, Math.min(rx * scaleX, ry * scaleY))

  const [ox, oy] = fixedPoint
  const [nx, ny] = getVectorToCenter(fixedAnchor, rotation, size, size)

  return [ox + size * nx, oy + size * ny]
}
